import type { PlanVo } from "@/api";
import creditIcon from "@/assets/images/credit-icon.png";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { configStore } from "@/store/configStore";
import { firstPurchasePromoStore } from "@/store/firstPurchasePromo";
import { pricingStore } from "@/store/pricing";
import { formatNumberWithCommas } from "@/util/formatNumber";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import Image from "next/image";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";
import { isFreePlan } from "../Pricing.helpers";
import DiamondIcon from "../assets/most-popular-diamond.svg";
import { PlanBenefitsSection } from "./PlanBenefitsSection";
import { PlanButton } from "./PlanButton";
import styles from "./PlanCard.module.css";

type BillingCycle = "MONTHLY" | "YEARLY";
interface PlanCardProps {
  plan: PlanVo;
  billingCycle: BillingCycle;
  freeCreditAmount?: number;
  index: number;
  plans: PlanVo[];
  currentPlanCode?: string;
  yearlyDiscount?: string;
  subscriptionBonus?: number;
}

export const PlanCard = observer(function PlanCard({
  plan,
  billingCycle,
  freeCreditAmount,
  index,
  plans,
  currentPlanCode,
  yearlyDiscount,
  subscriptionBonus = 0,
}: PlanCardProps) {
  const isCurrentPlan = plan.planCode === currentPlanCode;

  // Get plan configuration from store
  const planConfig = configStore.getPricingPlanConfig(plan.memberCode || "");

  // Get credit tooltips from config, fallback to plan.tooltipForCredit
  const creditTooltips = planConfig?.creditTooltips || [];

  const cardClassName = useMemo(() => {
    if (isFreePlan(plan)) {
      return "bg-[#212129]";
    }

    if (plan.recommend) {
      return styles.recommendCard;
    }

    return "bg-[#212129]";
  }, [plan]);

  const monthyCreditsClass = useMemo(() => {
    if (isFreePlan(plan)) {
      return "bg-[rgba(110,110,110,0.26)]";
    }

    if (plan.recommend) {
      return styles.recommendMonthlyCredits;
    }

    return styles.subscriptionMonthlyCredit;
  }, [plan]);

  const tagConfig = useMemo(() => {
    if (isFreePlan(plan)) return null;

    // 如果进入红包倒计时且有优惠券资格
    if (firstPurchasePromoStore.shouldShowPromo) {
      return {
        text: "Limited Offer",
        className: "bg-[#FF6752] text-white",
      };
    }

    // 没有进入倒计时
    if (billingCycle === "YEARLY") {
      return {
        text: `Save ${yearlyDiscount?.replace("-", "")}`,
        className: "bg-[#5251F6] text-white",
      };
    }

    // Monthly 且没有倒计时时不展示
    return null;
  }, [
    billingCycle,
    plan,
    firstPurchasePromoStore.shouldShowPromo,
    yearlyDiscount,
  ]);

  const priceInfo = useMemo(() => {
    const rawPrice = Number(plan.price || 0);
    const monthlyPrice = billingCycle === "YEARLY" ? rawPrice / 12 : rawPrice;

    // Find the corresponding monthly plan for comparison on Yearly tab
    const monthlyPlan = (pricingStore.planPage.planList || []).find(
      (p: PlanVo) =>
        p.memberCode === plan.memberCode && p.intervalType === "MONTHLY",
    );
    const monthlyPlanPrice = monthlyPlan
      ? Number(monthlyPlan.price || 0)
      : monthlyPrice;

    const formatPrice = (price: number) => {
      // If it's a whole number, show it as an integer, otherwise show up to 1 decimal place
      if (price % 1 === 0) return price.toFixed(0);
      return price.toFixed(1).replace(/\.0$/, "");
    };

    // Case 1: Active Coupon (Surprise Red Packet etc.)
    if (
      firstPurchasePromoStore.shouldShowPromo &&
      firstPurchasePromoStore.couponData?.discount?.value &&
      !isFreePlan(plan)
    ) {
      const discountPercent = firstPurchasePromoStore.couponData.discount.value;
      const discountedMonthlyPrice =
        (monthlyPrice * (100 - discountPercent)) / 100;

      return {
        // As requested: "原价需要展示年的费用 / 12"
        originalMonthlyPrice: formatPrice(monthlyPrice),
        discountedMonthlyPrice: formatPrice(discountedMonthlyPrice),
        originalTotalPrice: formatPrice(rawPrice),
        discountedTotalPrice: formatPrice(
          rawPrice * (1 - discountPercent / 100),
        ),
        hasDiscount: true,
        isCouponActive: true,
      };
    }

    // Case 2: Yearly plan without coupon - show comparison with Monthly plan price
    if (billingCycle === "YEARLY" && monthlyPlanPrice > monthlyPrice) {
      return {
        originalMonthlyPrice: formatPrice(monthlyPlanPrice),
        discountedMonthlyPrice: formatPrice(monthlyPrice),
        hasDiscount: true,
        isCouponActive: false,
      };
    }

    // Case 3: Default (Monthly plan or no significant saving to show)
    return {
      originalMonthlyPrice: formatPrice(monthlyPrice),
      hasDiscount: false,
      isCouponActive: false,
    };
  }, [
    plan,
    billingCycle,
    firstPurchasePromoStore.shouldShowPromo,
    firstPurchasePromoStore.couponData,
    pricingStore.planPage.planList,
  ]);

  return (
    <div
      className={classNames(
        "relative w-full sm:w-[374px] h-full",
        plan.recommend ? "mt-9 lg:mt-0" : "",
      )}
    >
      {plan.recommend && (
        <div className="absolute -left-[4px] -right-[4px] -bottom-[4px] -top-[46px] rounded-[20px] bg-primary z-1 text-center">
          <div className="h-[46px] text-white text-base font-bold flex justify-center items-center gap-2">
            <DiamondIcon className="w-4 h-4" />
            <span>Most Popular</span>
          </div>
        </div>
      )}

      <div
        className={classNames(
          "relative h-full z-10 rounded-[20px] p-6 flex flex-col",
          cardClassName,
        )}
      >
        <h2 className="flex items-center">
          <span className="text-base font-bold text-white">
            {plan.memberShipName}
          </span>

          {tagConfig && (
            <span
              className={twMerge(
                "ml-1 h-5 px-2 rounded-[6px] flex items-center justify-center text-xs font-medium",
                tagConfig.className,
              )}
            >
              {tagConfig.text}
            </span>
          )}
        </h2>

        <div className="flex items-end text-sm text-text-primary mt-1">
          {priceInfo.hasDiscount && (
            <span className="text-[42px] font-medium text-[#FF8373] line-through mr-2">
              ${priceInfo.originalMonthlyPrice}
            </span>
          )}
          <span className="text-[42px] font-medium ">
            $
            {priceInfo.hasDiscount
              ? priceInfo.discountedMonthlyPrice
              : priceInfo.originalMonthlyPrice}
          </span>
          <span className="text-base font-normal mb-[10px]">/month</span>
        </div>

        <p className="text-text-tertiary text-xs font-normal overflow-hidden text-ellipsis line-clamp-2">
          {isFreePlan(plan)
            ? "For individuals getting started and exploring."
            : priceInfo.isCouponActive
              ? billingCycle === "YEARLY"
                ? `Billed for 12 months $${priceInfo.discountedTotalPrice} first year, then $${priceInfo.originalTotalPrice}/year`
                : `Renews at $${priceInfo.originalTotalPrice}/mo next month`
              : billingCycle === "YEARLY"
                ? "Billed for 12 months."
                : "Billed monthly."}
        </p>

        <PlanButton
          className="mt-6"
          plan={plan}
          isCurrentPlan={isCurrentPlan}
          subscriptionBonus={subscriptionBonus}
          onClick={() => {
            if (isCurrentPlan) {
              return;
            }

            logger.event(VidMuseEvent.PricingPagePlanClick, {
              billing_cycle: billingCycle,
              plan_code: plan.planCode,
            });

            if (isFreePlan(plan)) {
              window.location.href = "/";
              return;
            }

            const couponId = firstPurchasePromoStore.shouldShowPromo
              ? firstPurchasePromoStore.couponData?.id
              : undefined;
            const checkoutUrl = `/payment/sessions/checkout?priceId=${plan.priceId}${
              couponId ? `&couponId=${couponId}` : ""
            }`;

            window.open(checkoutUrl, "_blank", "noopener,noreferrer");
          }}
        />

        <div className={classNames("rounded-xl mt-6 p-3", monthyCreditsClass)}>
          <p
            className={classNames("flex items-center gap-1 text-text-primary")}
          >
            <Image src={creditIcon} alt="credit" width={14} height={14} />
            <span className="text-base font-medium">
              {formatNumberWithCommas(
                isFreePlan(plan)
                  ? freeCreditAmount || ""
                  : plan.creditAmount || "",
              )}
            </span>
            {isFreePlan(plan) ? (
              <span>one-time credits</span>
            ) : (
              <span>monthly credits</span>
            )}
          </p>

          <div className="mt-1 flex flex-col items-start pl-[5px]">
            {creditTooltips.map((tooltip, index) => (
              <p
                key={`tooltip-${index}`}
                className="flex items-start gap-[10px]"
              >
                <span className="mt-2 w-1 h-1 rounded-full bg-text-tertiary block"></span>
                <span className="text-xs font-normal leading-normal text-text-tertiary">
                  {tooltip}
                </span>
              </p>
            ))}
          </div>
        </div>

        <PlanBenefitsSection planConfig={planConfig} />
      </div>
    </div>
  );
});
